// "Arrived" gate for the pre-encounter rail (PROD-1256). Mirrors Flutter
// appointment_model.dart:523 `isArrived` — the patient is present (Arrived/CheckedIn) or the
// visit is done (Fulfilled), so rescheduling no longer applies. Used to hide the rail's
// Reschedule action once the appointment has reached one of these states.
import type { AppointmentStatusCode } from '@/types/appointment-api';

const ARRIVED_STATUSES: ReadonlySet<string> = new Set<AppointmentStatusCode>([
  'Arrived',
  'CheckedIn',
  'Fulfilled',
]);

export function isArrivedStatus(statusCode: string | null | undefined): boolean {
  return !!statusCode && ARRIVED_STATUSES.has(statusCode);
}

/**
 * The pre-encounter Start action is gated on appointment status (Flutter
 * `pre_encounter_widget.dart:295-312`): Start is enabled ONLY when the appointment is
 * Arrived (Flutter skips its error switch when `canEditVitals`). Every other KNOWN status
 * is blocked — Booked/Pending/Proposed and Cancelled/NoShow get their specific messages,
 * and any other non-arrived code (Waitlist, EnteredInError, or an unrecognised status —
 * Flutter's `default: "Loading"`) gets the generic update-status prompt. Returns the reason
 * to DISABLE Start (and to gate the create-on-open effect), or `undefined` when Start is allowed.
 *
 * A MISSING status (null/undefined/empty) returns `undefined` (no block) — a deep-link that
 * lacks the appointment status must not strand the user; the caller's "Preparing encounter…"
 * path applies instead. (Only a truly-absent status degrades; a known non-arrived one blocks.)
 */
export function appointmentStartBlockReason(
  statusCode: string | null | undefined,
): string | undefined {
  if (!statusCode || isArrivedStatus(statusCode)) return undefined;
  switch (statusCode) {
    case 'Cancelled':
      return 'Appointment is cancelled — update the appointment status to start the encounter.';
    case 'NoShow':
      return 'Patient is unavailable — update the appointment status to start the encounter.';
    default:
      // Booked / Pending / Proposed, and any other non-arrived code (Waitlist /
      // EnteredInError / unrecognised) — Flutter shows "Loading" here; we surface the
      // generic update-status prompt (still a disabled Start, matching "not startable").
      return 'Please update the appointment status to start the encounter.';
  }
}

/**
 * The chief-complaint lock — a DIFFERENT rule from `isArrivedStatus`, and deliberately so.
 *
 * Flutter's `ChiefComplaintWidget.enable` (`chief_complaint_widget.dart:60-71`) switches on the
 * appointment status and disables for Booked / Pending / Proposed only:
 *
 *     switch (status?.code) {
 *       case Booked: case Pending: case Proposed: return false;
 *       default: return true;
 *     }
 *
 * So Cancelled and NoShow leave the field EDITABLE, where `isArrivedStatus` would call them
 * non-arrived and lock it. Reusing the arrived gate here would have been the obvious shortcut and
 * would have silently changed behaviour on those two statuses. An UNKNOWN status stays editable,
 * matching Flutter's `default: true` — the field does not lock itself while the status loads.
 */
const CC_LOCKED_STATUSES: ReadonlySet<string> = new Set(['Booked', 'Pending', 'Proposed']);

export function isChiefComplaintLocked(statusCode: string | null | undefined): boolean {
  return !!statusCode && CC_LOCKED_STATUSES.has(statusCode);
}
